import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, IconButton, InputAdornment, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Theme, Typography, CircularProgress, Avatar, useTheme,
} from '@mui/material';
import { Delete, Edit, Email, FileUpload, People as PeopleIcon, Person, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Operator } from '../../types';
import { operatorApi } from '../../services/api';
import { extractApiError, unwrapSingleResponse } from '../../apiUnwrapper/apiUnwrapper';

import AddNewOperatorPopUp from '../../components/AddNewOperatorPopUp';
import ConfirmDialog from '../../components/ConfirmDialog';

import { useOperatorData } from '../../hooks/UseOperatorData';
import { useBulkUpload } from '../../hooks/Usebulkupload';
import { useConfirmDialog } from '../../hooks/UseConfirmDialog';
import {
  gradientButtonSx, PageHeader, StatusBanner, UploadProgressBar, EmptyTableRow,
} from '../../components/SharedPageParts';
import { useAuth } from '../../services/AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** null = add mode, defined = edit mode */
type EditTarget = Operator | null;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OperatorsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageOperators = user?.roleName === 'Admin';
  const { enqueueSnackbar } = useSnackbar();

  // ── Operator data ─────────────────────────────────────────────────────────
  const {
    operators, meta, loading, error, setError,
    loadOperators, searchOperators,
  } = useOperatorData();

  // ── Bulk upload ───────────────────────────────────────────────────────────
  const { uploadState, handleFileChange, handleUpload } = useBulkUpload(
    (file) => operatorApi.bulkUploadOperators(file),
    () => loadOperators(meta.page, meta.size),
  );

  // ── Confirm dialog ────────────────────────────────────────────────────────
  const { confirmState, confirm, closeConfirm, executeConfirm } = useConfirmDialog();

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]             = useState('');
  const [editTarget, setEditTarget]             = useState<EditTarget>(null);
  const [operatorFormOpen, setOperatorFormOpen] = useState(false);
  const [fieldErrors, setFieldErrors]           = useState<Record<string, string>>({});

  const isEditMode = editTarget !== null;

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  useEffect(() => {
    loadOperators(1, meta.size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Search & pagination ───────────────────────────────────────────────────

  const handleSearch = useCallback(() => {
    if (searchTerm.trim()) searchOperators(searchTerm, 1, meta.size);
    else loadOperators(1, meta.size);
  }, [searchTerm, meta.size, searchOperators, loadOperators]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    if (searchTerm.trim()) searchOperators(searchTerm, newPage + 1, meta.size);
    else loadOperators(newPage + 1, meta.size);
  }, [searchTerm, meta.size, searchOperators, loadOperators]);

  const handleRowsPerPageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(e.target.value, 10);
    if (searchTerm.trim()) searchOperators(searchTerm, 1, newSize);
    else loadOperators(1, newSize);
  }, [searchTerm, searchOperators, loadOperators]);

  // ── Form open/close ───────────────────────────────────────────────────────

  const openAddForm = useCallback(() => {
    setEditTarget(null);
    setFieldErrors({});
    setOperatorFormOpen(true);
  }, []);

  const openEditForm = useCallback((operator: Operator) => {
    setEditTarget(operator);
    setFieldErrors({});
    setOperatorFormOpen(true);
  }, []);

  const closeOperatorForm = useCallback(() => {
    setOperatorFormOpen(false);
    setFieldErrors({});
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async (payload: Operator) => {
    try {
      if (isEditMode && payload.id !== 0) {
        const result = unwrapSingleResponse<Operator>(await operatorApi.updateOperator(payload));
        enqueueSnackbar(`Operator updated — ${result?.userName}`, { variant: 'success' });
      } else {
        const result = unwrapSingleResponse<Operator>(await operatorApi.createOperator(payload));
        enqueueSnackbar(`Operator created — ${result?.firstName} ${result?.lastName}`, { variant: 'success' });
      }

      if (searchTerm.trim()) await searchOperators(searchTerm, meta.page, meta.size);
      else await loadOperators(meta.page, meta.size);

      closeOperatorForm();
    } catch (err: any) {
      const { message, fieldErrors: apiFieldErrors } = extractApiError(err);
      setFieldErrors(apiFieldErrors ?? {});
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [isEditMode, searchTerm, meta.page, meta.size, searchOperators, loadOperators, closeOperatorForm, enqueueSnackbar]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteRequest = useCallback((id: number) => {
    confirm('Are you sure you want to delete this operator?', async () => {
      try {
        await operatorApi.deleteOperator(id);
        enqueueSnackbar('Operator deleted successfully', { variant: 'success' });
        await loadOperators(meta.page, meta.size);
      } catch (err) {
        enqueueSnackbar(`Failed to delete operator: ${err}`, { variant: 'error' });
        setError(`Error deleting operator: ${err}`);
      }
    });
  }, [confirm, enqueueSnackbar, loadOperators, meta.page, meta.size, setError]);

  // ── Loading screen ────────────────────────────────────────────────────────

  if (loading && operators.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, backgroundColor: theme.palette.background.default }}>
        <CircularProgress sx={{ color: '#667eea' }} />
        <Typography variant="h6" color="text.primary">Loading operators…</Typography>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <Box sx={{ px: 2, mx: 'auto' }}>

        <PageHeader
          title="Operator Management"
          Icon={PeopleIcon}
          onBack={() => navigate('/commondata')}
          backLabel="Back to Common Data"
          subtitle="Manage operators and their information."
        />

        <StatusBanner error={error} status={null} />
        {canManageOperators && (
          <>
            <StatusBanner error={null} status={uploadState.status} />
            {uploadState.error && <StatusBanner error={uploadState.error} status={null} />}
          </>
        )}

        {/* Toolbar */}
        <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search operators by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearch} edge="end">
                        <Search sx={{ color: theme.palette.text.secondary }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { backgroundColor: theme.palette.background.default, color: theme.palette.text.primary },
                }}
                sx={{
                  flexGrow: 1, minWidth: 250,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.divider },
                    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                    '&.Mui-focused fieldset': { borderColor: '#667eea' },
                  },
                  '& .MuiInputBase-input': { color: theme.palette.text.primary },
                }}
              />

              {canManageOperators && (
                <>
                  <Button variant="contained" sx={gradientButtonSx} onClick={openAddForm}>
                    Add New Operator
                  </Button>

                  <Button variant="contained" component="label" startIcon={<FileUpload />} sx={gradientButtonSx}>
                    Bulk Upload
                    <input type="file" accept=".csv,.xlsx,.xls" hidden onChange={handleFileChange} />
                  </Button>

                  {uploadState.file && (
                    <Button
                      variant="contained"
                      disabled={uploadState.uploading}
                      onClick={handleUpload}
                      sx={gradientButtonSx}
                    >
                      {uploadState.uploading ? 'Uploading…' : 'Upload'}
                    </Button>
                  )}

                  <Button variant="outlined" sx={gradientButtonSx}
                    onClick={() => operatorApi.downloadTemplate()}>
                    Download Template
                  </Button>
                </>
              )}
            </Box>

            {canManageOperators && uploadState.fileName && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: <strong>{uploadState.fileName}</strong>
              </Typography>
            )}

            {canManageOperators && (
              <UploadProgressBar uploading={uploadState.uploading} progress={uploadState.progress} />
            )}
          </CardContent>
        </Card>

        {/* Operators table */}
        <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" color="text.primary">
                Operators ({meta.total})
              </Typography>
              <TablePagination
                component="div"
                count={meta.total}
                page={meta.page - 1}
                onPageChange={handlePageChange}
                rowsPerPage={meta.size}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 15, 20]}
                labelRowsPerPage="Per page"
              />
            </Box>

            <TableContainer component={Paper} variant="outlined"
              sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.background.paper }}>
                    {['Avatar', 'Username', 'Name', 'Email', 'Status', ...(canManageOperators ? ['Actions'] : [])].map((h) => (
                      <TableCell key={h} align={h === 'Actions' ? 'center' : 'left'}
                        sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operators.length === 0
                    ? <EmptyTableRow colSpan={canManageOperators ? 6 : 5} hasSearch={!!searchTerm.trim()} Icon={Person} entityName="operators" />
                    : operators.map((operator) => (
                        <OperatorRow
                          key={operator.id}
                          operator={operator}
                          canManage={canManageOperators}
                          onEdit={openEditForm}
                          onDelete={handleDeleteRequest}
                        />
                      ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={meta.total}
              page={meta.page - 1}
              onPageChange={handlePageChange}
              rowsPerPage={meta.size}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 15, 20]}
              labelRowsPerPage="Per page"
            />
          </CardContent>

          <ConfirmDialog
            open={confirmState.open}
            onClose={closeConfirm}
            onConfirm={executeConfirm}
            title="Confirm Delete"
            message={confirmState.message}
          />
        </Card>
      </Box>

      {/* Popup */}
      <AddNewOperatorPopUp
        open={operatorFormOpen}
        onClose={closeOperatorForm}
        onSubmit={handleSubmit}
        selectedOperator={editTarget ?? undefined}
        isEditMode={isEditMode}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// OperatorRow — memoized to prevent full-table re-renders
// ---------------------------------------------------------------------------

interface OperatorRowProps {
  operator: Operator;
  canManage: boolean;
  onEdit: (o: Operator) => void;
  onDelete: (id: number) => void;
}

const OperatorRow: React.FC<OperatorRowProps> = React.memo(({ operator, canManage, onEdit, onDelete }) => {
  const theme: Theme = useTheme();
  const cell = { borderBottom: 'none' };

  // Build avatar initials — prefer last name, then first, then username
  const initials = (
    operator.lastName?.charAt(0) ??
    operator.firstName?.charAt(0) ??
    operator.userName.charAt(0)
  ).toUpperCase();

  return (
    <TableRow hover sx={{
      '&:hover': { backgroundColor: theme.palette.background.paper },
      borderBottom: `1px solid ${theme.palette.divider}`,
    }}>
      <TableCell sx={cell}>
        <Avatar sx={{ bgcolor: '#667eea' }}>{initials}</Avatar>
      </TableCell>
      <TableCell sx={cell}>
        <Typography variant="subtitle2" color="text.primary">{operator.userName}</Typography>
      </TableCell>
      <TableCell sx={cell}>
        <Typography variant="subtitle2" color="text.primary">
          {[operator.firstName, operator.lastName].filter(Boolean).join(' ')}
        </Typography>
      </TableCell>
      <TableCell sx={cell}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Email sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          <Typography color="text.secondary">{operator.email}</Typography>
        </Box>
      </TableCell>
      <TableCell sx={cell}>
        <Chip
          label={operator.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={operator.isActive ? 'success' : 'default'}
        />
      </TableCell>
      {canManage && (
        <TableCell align="center" sx={cell}>
          <IconButton size="small" sx={{ color: '#06beb6' }}
            onClick={() => onEdit(operator)}
            aria-label={`Edit ${operator.userName}`}>
            <Edit />
          </IconButton>
          <IconButton size="small" sx={{ color: '#ef4444' }}
            onClick={() => onDelete(operator.id)}
            aria-label={`Delete ${operator.userName}`}>
            <Delete />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );
});

OperatorRow.displayName = 'OperatorRow';

export default OperatorsPage;